(function (root, factory) {
  'use strict';
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.SeeGartPreviewCore = factory();
  }
}(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var SCENE_WIDTH = 1536;
  var SCENE_HEIGHT = 1024;

  function finite(value) {
    return typeof value === 'number' && Number.isFinite(value);
  }

  function positive(value) {
    return finite(value) && value > 0;
  }

  function clamp(value, minimum, maximum, fallback) {
    return Math.min(maximum, Math.max(minimum, finite(value) ? value : fallback));
  }

  function invalidSize(raw, reason) {
    return { widthCm: 0, heightCm: 0, raw: raw, valid: false, reason: reason };
  }

  /**
   * Read exact catalog metric dimensions. `landscape` is an explicit caller
   * assertion backed by catalog evidence, never an inference from uploaded art.
   * Rounded catalog centimeters remain unchanged; do not reconvert inches.
   */
  function parseSize(raw, orientation, supported) {
    if (supported === undefined) supported = true;
    if (typeof raw !== 'string' || !raw.trim()) {
      return invalidSize(typeof raw === 'string' ? raw : '', 'No supported size label was supplied.');
    }
    if (!supported) return invalidSize(raw, 'Calibrated preview is not enabled for this product.');
    if (/2\s*p\b/i.test(raw)) {
      return invalidSize(raw, 'Two-panel dimensions and spacing need confirmation before scale preview.');
    }

    var parts = raw.split('/');
    if (parts.length !== 2) {
      return invalidSize(raw, 'The size needs an unambiguous centimeter pair after the slash.');
    }
    var metric = /^\s*(\d+(?:\.\d+)?)\s*[x\u00d7]\s*(\d+(?:\.\d+)?)\s*cm\s*$/i.exec(parts[1]);
    if (!metric) return invalidSize(raw, 'The centimeter dimensions could not be read safely.');
    var first = Number(metric[1]);
    var second = Number(metric[2]);
    if (!positive(first) || !positive(second)) {
      return invalidSize(raw, 'Dimensions must be finite positive numbers.');
    }

    var width;
    var height;
    var axes = /^\s*\d+(?:\.\d+)?\s*["'\u2019\u2033]*\s*([hw])\s*[x\u00d7]\s*\d+(?:\.\d+)?\s*["'\u2019\u2033]*\s*([hw])\s*$/i.exec(parts[0]);
    if (axes && axes[1].toLowerCase() !== axes[2].toLowerCase()) {
      height = axes[1].toLowerCase() === 'h' ? first : second;
      width = axes[1].toLowerCase() === 'w' ? first : second;
    } else if (first === second && !/[hw]/i.test(parts[0])) {
      width = first;
      height = second;
    } else if (orientation === 'landscape' && first < second && !/[hw]/i.test(parts[0])) {
      height = first;
      width = second;
    } else {
      return invalidSize(raw, 'Height and width are not confirmed for this size label.');
    }
    return { widthCm: width, heightCm: height, raw: raw, valid: true, reason: '' };
  }

  function invalidCrop(reason) {
    return { sx: 0, sy: 0, sw: 0, sh: 0, valid: false, reason: reason };
  }

  /**
   * Cover crop in original-image pixels. Focus is a normalized point in the
   * original image; the crop centers on it, then stops at image boundaries.
   * Nonfinite optional controls reset to their defaults. Invalid dimensions
   * return a finite zero rectangle with valid:false, rather than throwing.
   */
  function cropRect(imageW, imageH, targetW, targetH, zoom, focusX, focusY) {
    if (![imageW, imageH, targetW, targetH].every(positive)) {
      return invalidCrop('Image and target dimensions must be finite positive numbers.');
    }
    zoom = clamp(zoom, 1, 3, 1);
    focusX = clamp(focusX, 0, 1, 0.5);
    focusY = clamp(focusY, 0, 1, 0.5);
    var targetRatio = targetW / targetH;
    var sourceRatio = imageW / imageH;
    if (!positive(targetRatio) || !positive(sourceRatio)) {
      return invalidCrop('The image aspect ratio is outside the supported numeric range.');
    }
    var sw = sourceRatio > targetRatio ? imageH * targetRatio : imageW;
    var sh = sourceRatio > targetRatio ? imageH : imageW / targetRatio;
    sw = Math.min(imageW, sw / zoom);
    sh = Math.min(imageH, sh / zoom);
    if (!positive(sw) || !positive(sh)) {
      return invalidCrop('The crop is outside the supported numeric range.');
    }
    var sx = clamp(imageW * focusX - sw / 2, 0, imageW - sw, 0);
    var sy = clamp(imageH * focusY - sh / 2, 0, imageH - sh, 0);
    return { sx: sx, sy: sy, sw: sw, sh: sh, valid: true, reason: '' };
  }

  function invalidScene(reason) {
    return {
      x: 0, y: 0, width: 0, height: 0, scalePxPerCm: 0,
      overflow: false, valid: false, reason: reason
    };
  }

  function normalizedBounds(values, length) {
    return Array.isArray(values) && values.length === length && values.every(function (value) {
      return finite(value) && value >= 0 && value <= 1;
    });
  }

  /**
   * Place physically scaled canvas inside the scene's safe wall. Coordinates
   * use a fixed 1536 x 1024 artboard; frames are not included in these sizes.
   * On an axis too small for the art, retain scale and center the overflow.
   */
  function sceneRect(size, room, position) {
    if (!size || size.valid === false || !positive(size.widthCm) || !positive(size.heightCm)) {
      return invalidScene('A confirmed positive canvas size is required.');
    }
    if (!room || !normalizedBounds(room.furnitureSpan, 2) ||
        room.furnitureSpan[1] <= room.furnitureSpan[0] || !positive(room.furnitureWidthCm)) {
      return invalidScene('The room furniture reference needs a valid span and width.');
    }
    if (!normalizedBounds(room.safeWall, 4) || room.safeWall[2] <= room.safeWall[0] ||
        room.safeWall[3] <= room.safeWall[1]) {
      return invalidScene('The room needs a valid safe-wall rectangle.');
    }
    var scale = SCENE_WIDTH * (room.furnitureSpan[1] - room.furnitureSpan[0]) / room.furnitureWidthCm;
    var width = size.widthCm * scale;
    var height = size.heightCm * scale;
    if (![scale, width, height].every(positive)) {
      return invalidScene('The scaled dimensions are outside the supported numeric range.');
    }
    var left = room.safeWall[0] * SCENE_WIDTH;
    var top = room.safeWall[1] * SCENE_HEIGHT;
    var wallW = (room.safeWall[2] - room.safeWall[0]) * SCENE_WIDTH;
    var wallH = (room.safeWall[3] - room.safeWall[1]) * SCENE_HEIGHT;
    position = position && typeof position === 'object' ? position : {};
    var spareX = wallW - width;
    var spareY = wallH - height;
    var x = left + spareX * (spareX < 0 ? 0.5 : clamp(position.x, 0, 1, 0.5));
    var y = top + spareY * (spareY < 0 ? 0.5 : clamp(position.y, 0, 1, 0.5));
    return {
      x: x, y: y, width: width, height: height, scalePxPerCm: scale,
      overflow: spareX < -0.000001 || spareY < -0.000001,
      valid: true, reason: ''
    };
  }

  /**
   * Overview camera for art that exceeds the original scene's clear wall.
   * Extend the virtual wall upward / sideways, never down into furniture, then
   * scale the complete room AND artwork with the same camera. Blank extension
   * areas must be drawn by the UI as an illustrative wall, not a real room photo.
   * Camera bounds do not depend on position, so dragging cannot change zoom.
   */
  function sceneLayout(size, room, position) {
    var original = sceneRect(size, room, position);
    var canvas = { width: SCENE_WIDTH, height: SCENE_HEIGHT };
    var identity = {
      scale: 1, translateX: 0, translateY: 0,
      worldBounds: [0, 0, SCENE_WIDTH, SCENE_HEIGHT]
    };
    if (!original.valid) {
      return {
        valid: false, reason: original.reason, overview: false, canvas: canvas,
        backgroundRect: { x: 0, y: 0, width: 0, height: 0 }, artRect: original,
        camera: identity, placementWall: [0, 0, 0, 0], positionRange: { x: 0, y: 0 }
      };
    }
    var left = room.safeWall[0] * SCENE_WIDTH;
    var top = room.safeWall[1] * SCENE_HEIGHT;
    var right = room.safeWall[2] * SCENE_WIDTH;
    var bottom = room.safeWall[3] * SCENE_HEIGHT;
    if (!original.overflow) {
      return {
        valid: true, reason: '', overview: false, canvas: canvas,
        backgroundRect: { x: 0, y: 0, width: SCENE_WIDTH, height: SCENE_HEIGHT },
        artRect: original, camera: identity, placementWall: [left, top, right, bottom],
        positionRange: { x: Math.max(0, right - left - original.width), y: Math.max(0, bottom - top - original.height) }
      };
    }

    // Leave a little movement room for oversized artwork. The bottom of the
    // safe wall remains the same, preserving the clearance above furniture.
    var movementSpace = 48;
    var extendedWidth = Math.max(right - left, original.width + movementSpace);
    var centerX = (left + right) / 2;
    left = centerX - extendedWidth / 2;
    right = centerX + extendedWidth / 2;
    top = Math.min(top, bottom - original.height - movementSpace);

    // Include the whole room and a border around the virtual wall. The common
    // camera has 24 screen pixels of side/top allowance; the room floor stays
    // anchored to the bottom edge rather than floating above a fake floor.
    var border = 24;
    var worldLeft = Math.min(0, left - border);
    var worldTop = Math.min(0, top - border);
    var worldRight = Math.max(SCENE_WIDTH, right + border);
    var worldBottom = SCENE_HEIGHT;
    var worldWidth = worldRight - worldLeft;
    var worldHeight = worldBottom - worldTop;
    var cameraScale = Math.min(1, (SCENE_WIDTH - 48) / worldWidth, (SCENE_HEIGHT - 24) / worldHeight);
    var tx = (SCENE_WIDTH - worldWidth * cameraScale) / 2 - worldLeft * cameraScale;
    var ty = SCENE_HEIGHT - worldBottom * cameraScale;
    var rangeX = Math.max(0, right - left - original.width);
    var rangeY = Math.max(0, bottom - top - original.height);
    position = position && typeof position === 'object' ? position : {};
    var artX = left + rangeX * clamp(position.x, 0, 1, 0.5);
    var artY = top + rangeY * clamp(position.y, 0, 1, 0.5);
    var artRect = {
      x: tx + artX * cameraScale, y: ty + artY * cameraScale,
      width: original.width * cameraScale, height: original.height * cameraScale,
      scalePxPerCm: original.scalePxPerCm * cameraScale,
      overflow: false, valid: true, reason: ''
    };
    if (![worldWidth, worldHeight, cameraScale, artRect.width, artRect.height, artRect.scalePxPerCm].every(positive) ||
        ![tx, ty, artRect.x, artRect.y].every(finite)) {
      var invalid = invalidScene('The overview is outside the supported numeric range.');
      return {
        valid: false, reason: invalid.reason, overview: false, canvas: canvas,
        backgroundRect: { x: 0, y: 0, width: 0, height: 0 }, artRect: invalid,
        camera: identity, placementWall: [0, 0, 0, 0], positionRange: { x: 0, y: 0 }
      };
    }
    return {
      valid: true, reason: '', overview: true, canvas: canvas,
      backgroundRect: { x: tx, y: ty, width: SCENE_WIDTH * cameraScale, height: SCENE_HEIGHT * cameraScale },
      artRect: artRect,
      camera: { scale: cameraScale, translateX: tx, translateY: ty, worldBounds: [worldLeft, worldTop, worldRight, worldBottom] },
      placementWall: [left, top, right, bottom],
      positionRange: { x: rangeX * cameraScale, y: rangeY * cameraScale }
    };
  }

  function invalidQuality(reason) {
    return { ppi: 0, status: 'low', message: reason };
  }

  /** Effective source resolution after the real crop; advisory, not approval. */
  function printQuality(imageWidth, imageHeight, crop, widthCm, heightCm) {
    if (![imageWidth, imageHeight, widthCm, heightCm].every(positive) || !crop ||
        crop.valid === false || ![crop.sx, crop.sy].every(finite) ||
        ![crop.sw, crop.sh].every(positive)) {
      return invalidQuality('Resolution cannot be assessed until the image, crop and canvas size are valid.');
    }
    var epsilon = Math.max(imageWidth, imageHeight) * 1e-10;
    if (crop.sx < 0 || crop.sy < 0 || crop.sx + crop.sw > imageWidth + epsilon ||
        crop.sy + crop.sh > imageHeight + epsilon) {
      return invalidQuality('The crop extends beyond the original image; resolution cannot be assessed.');
    }
    var ppi = Math.min(crop.sw / widthCm, crop.sh / heightCm) * 2.54;
    if (!positive(ppi)) return invalidQuality('Resolution is outside the supported numeric range.');
    // Allow only floating-point arithmetic noise at the exact advisory limits.
    var status = ppi >= 150 - 1e-9 ? 'good' : ppi >= 100 - 1e-9 ? 'review' : 'low';
    var messages = {
      good: 'Advisory: 150+ pixels per inch at this crop and size. Final print suitability needs production review.',
      review: 'Advisory: 100 to under 150 pixels per inch. Review the original at the selected size before printing.',
      low: 'Advisory: below 100 pixels per inch. A larger source image or smaller canvas may preserve more detail.'
    };
    return { ppi: ppi < Number.MAX_VALUE / 10 ? Math.round(ppi * 10) / 10 : ppi, status: status, message: messages[status] };
  }

  // Conservative photo-border detection. Only a strong rectangular subject on
  // a flat, near-white surround qualifies. This never segments a room scene.
  function photoBounds(pixels, targetAspect) {
    var fail = function(reason) { return {valid:false,reason:reason}; };
    var width=pixels&&pixels.width,height=pixels&&pixels.height,data=pixels&&pixels.data;
    if(!Number.isInteger(width)||!Number.isInteger(height)||width<16||height<16||width>512||height>512||!data||data.length!==width*height*4||!positive(targetAspect))return fail('invalid_pixels');
    var corners=[],base=[0,0,0];
    for(var cy of [0,height-3])for(var cx of [0,width-3])for(var y=cy;y<cy+3;y++)for(var x=cx;x<cx+3;x++){var i=(y*width+x)*4;if(data[i+3]<250)return fail('transparent_border');corners.push([data[i],data[i+1],data[i+2]]);}
    for(var c=0;c<3;c++)base[c]=corners.reduce(function(sum,p){return sum+p[c];},0)/corners.length;
    if(Math.min.apply(null,base)<230||Math.max.apply(null,base)-Math.min.apply(null,base)>18)return fail('not_light_border');
    if(corners.some(function(p){return Math.max(Math.abs(p[0]-base[0]),Math.abs(p[1]-base[1]),Math.abs(p[2]-base[2]))>10;}))return fail('uneven_border');
    var mask=new Uint8Array(width*height),rows=new Uint32Array(height),cols=new Uint32Array(width);
    for(var yy=0;yy<height;yy++)for(var xx=0;xx<width;xx++){var j=(yy*width+xx)*4,ink=data[j+3]>245&&Math.max(Math.abs(data[j]-base[0]),Math.abs(data[j+1]-base[1]),Math.abs(data[j+2]-base[2]))>=30;if(ink){mask[yy*width+xx]=1;rows[yy]++;cols[xx]++;}}
    var peakRow=Math.max.apply(null,rows),peakCol=Math.max.apply(null,cols);
    if(peakRow<width*.35||peakCol<height*.2)return fail('weak_subject');
    var top=rows.findIndex(function(n){return n>=peakRow*.65;}),bottom=height-1,left=cols.findIndex(function(n){return n>=peakCol*.65;}),right=width-1;
    while(bottom>=0&&rows[bottom]<peakRow*.65)bottom--;while(right>=0&&cols[right]<peakCol*.65)right--;
    var bw=right-left+1,bh=bottom-top+1,area=bw*bh/(width*height);
    if(left<2||top<2||right>width-3||bottom>height-3||area<.20||area>.93||bw<width*.35||bh<height*.2)return fail('unsafe_margin');
    if(Math.abs(Math.log((bw/bh)/targetAspect))>.16)return fail('aspect_mismatch');
    function rowCover(y){var total=0;for(var x=left;x<=right;x++)total+=mask[y*width+x];return total/bw;}
    function colCover(x){var total=0;for(var y=top;y<=bottom;y++)total+=mask[y*width+x];return total/bh;}
    if(Math.max(rowCover(top),rowCover(Math.min(top+1,bottom)))<.8||Math.max(rowCover(bottom),rowCover(Math.max(bottom-1,top)))<.8||Math.max(colCover(left),colCover(Math.min(left+1,right)))<.8||Math.max(colCover(right),colCover(Math.max(right-1,left)))<.8)return fail('not_rectangular');
    var outside=0;for(var y=0;y<height;y++)for(var x=0;x<width;x++)if(x<left-2||x>right+2||y<top-2||y>bottom+2)outside+=mask[y*width+x];
    if(outside/(width*height)>.025)return fail('other_content');
    // One sample pixel of breathing room keeps anti-aliased subject edges.
    return {valid:true,x:(left-1)/width,y:(top-1)/height,width:(bw+2)/width,height:(bh+2)/height,reason:'flat-light-photo-border'};
  }

  return Object.freeze({
    parseSize: parseSize,
    cropRect: cropRect,
    sceneRect: sceneRect,
    sceneLayout: sceneLayout,
    printQuality: printQuality,
    photoBounds: photoBounds,
    scaledSourceRect: scaledSourceRect
  });
  /** Map reviewed original-file pixels onto the decoded CDN rendition. */
  function scaledSourceRect(rect, sourceSize, imageWidth, imageHeight) {
    if (!rect || ![rect.x,rect.y].every(finite) || ![rect.width,rect.height,imageWidth,imageHeight].every(positive)) return null;
    var width=sourceSize?.width||imageWidth,height=sourceSize?.height||imageHeight;
    if (![width,height].every(positive)||rect.x<0||rect.y<0||rect.x+rect.width>width||rect.y+rect.height>height) return null;
    // Resizing is valid; a differently cropped CDN rendition is not.
    if (Math.abs(Math.log((imageWidth/imageHeight)/(width/height)))>.003) return null;
    return {x:rect.x*imageWidth/width,y:rect.y*imageHeight/height,width:rect.width*imageWidth/width,height:rect.height*imageHeight/height};
  }
}));
